/**
 * `useShopifyOauthReturn` — ADR-0052 §4.2.3. Shared post-Shopify-OAuth
 * polling primitive consumed by both `ShopifyConnectPanel` (which
 * surfaces the connected-pill state on its own card) AND
 * `InstallVerifyStep` (which advances the onboarding machine to step 4
 * once the connector flips to `connected` AND the site's status — the
 * load-bearing condition — has flipped to a verified tier).
 *
 * The polling logic mirrors what `ShopifyConnectPanel` previously
 * inlined: 30s budget, 2s step, `polling` ref to defend against
 * stale-closure cleanup. The hook surfaces only the read state — it
 * does not call any "advance" / "navigate" side effect; consumers gate
 * advance on the REAL server-side `site.status`, never on the URL flag
 * alone (mirrors the ADR-0050 Stripe server-truth lesson — optimistic
 * URL flags are SUPERSEDED by server truth).
 *
 * Wire contract: same `GET /v1/sites/:site_id/connector?kind=shopify`
 * the panel already uses. No new endpoint, no new secret.
 *
 * Stale-flag cleanup (defect D, 2026-06-04): after the poll reaches ANY
 * terminal state (`connected` / `error` / `unconfirmed`) the hook strips
 * `?connected=shopify` and the companion `?site=<site_id>` query params
 * from the URL via `history.replaceState`. Without this, a stale URL
 * flag left over from a prior round-trip that never confirmed (browser
 * back, tab restore, manual revisit) re-arms the 30s poll on every
 * subsequent render and grays out the Connect Shopify button for the
 * full budget on a site that has no integration row at all. The flag is
 * a one-shot trigger; clearing it after the terminal poll keeps the
 * button reliably clickable.
 *
 * No-row-is-not-terminal (defect D): the poll treats a
 * `{kind:null,status:null}` response as "no integration yet, keep
 * waiting" rather than a terminal — the merchant just hasn't connected
 * yet, and that's the COMMON case here. The button stays clickable
 * because the panel's disabled gate only fires on `'connecting'` /
 * `'connected'`; the hook never reports those for a no-row site unless
 * the URL flag is present (a real return-from-OAuth signal).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { SiteId } from '@sill/types';

import { useApi } from '../api/api-context.js';

const MAX_MS = 30_000;
const STEP_MS = 2_000;

/**
 * Strip the post-OAuth return params (`?connected=shopify` + the
 * companion `?site=<site_id>` carried by the same redirect) from the
 * current URL. Called once a terminal state lands so subsequent
 * renders / revisits don't re-arm a phantom poll on a site that no
 * longer has an in-flight connect attempt. All other query params and
 * the path/hash are preserved verbatim.
 */
function clearOauthReturnFlag(): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    let mutated = false;
    if (url.searchParams.has('connected')) {
      url.searchParams.delete('connected');
      mutated = true;
    }
    if (url.searchParams.has('site')) {
      url.searchParams.delete('site');
      mutated = true;
    }
    if (!mutated) return;
    // Use replaceState so the entry doesn't pollute history — the
    // merchant's Back button must still land where it would have.
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  } catch {
    /* private-mode / sandbox iframe — degrade silently */
  }
}

export type ShopifyOauthReturnStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'error'
  | 'unconfirmed';

export interface UseShopifyOauthReturnState {
  /** True when `?connected=shopify` is present on the URL at mount. */
  readonly returnedFromOauth: boolean;
  /** Current poll state. `'connecting'` from the first poll until the
   *  connector row reports `connected` / `error` OR the 30s budget
   *  exhausts. `'connected'` is the success terminal; `'error'` the
   *  failure terminal; `'unconfirmed'` is the budget-exhausted terminal
   *  (we never saw a confirmed connect — surface an actionable
   *  retry-or-try-again state, NOT a frozen "Connecting…"). */
  readonly connectStatus: ShopifyOauthReturnStatus;
  /** True while the poll loop is running. */
  readonly polling: boolean;
}

/**
 * Reads the URL once on mount for `?connected=shopify`. When present,
 * starts a bounded poll against the site's Shopify-connector status.
 *
 * The returned `connectStatus` is informational; the consumer that
 * needs to ADVANCE onboarding must additionally gate on the real
 * `site.status` (the OAuth callback writes the proof row + flips the
 * site to `discovery_active` / `transactional_active` inside the same
 * tenant tx — that status is the load-bearing condition). The URL flag
 * alone is not authoritative; a merchant who manually appends
 * `?connected=shopify` gains nothing.
 */
export function useShopifyOauthReturn(
  site_id: SiteId | null,
): UseShopifyOauthReturnState {
  const api = useApi();
  const [connectStatus, setConnectStatus] =
    useState<ShopifyOauthReturnStatus>('idle');
  const [polling, setPolling] = useState(false);
  /** Mirror of `polling` for the cleanup callback — same stale-closure
   *  defense pattern ShopifyConnectPanel inlined pre-extraction. */
  const pollingRef = useRef(false);

  const returnedFromOauth = useMemo<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      const params = new URL(window.location.href).searchParams;
      return params.get('connected') === 'shopify';
    } catch {
      return false;
    }
  }, []);

  const startPolling = useCallback((): void => {
    if (pollingRef.current) return;
    if (site_id === null) return;
    pollingRef.current = true;
    setPolling(true);
    const started = Date.now();
    const tick = async (): Promise<void> => {
      if (!pollingRef.current) return;
      try {
        const status = await api.sites.getConnectorStatus(site_id, 'shopify');
        if (status.kind === 'shopify' && status.status === 'connected') {
          pollingRef.current = false;
          setPolling(false);
          setConnectStatus('connected');
          // Defect D: strip the one-shot return flag so a re-render or
          // revisit doesn't re-arm a phantom poll.
          clearOauthReturnFlag();
          return;
        }
        if (status.kind === 'shopify' && status.status === 'error') {
          pollingRef.current = false;
          setPolling(false);
          setConnectStatus('error');
          clearOauthReturnFlag();
          return;
        }
        // `{kind:null,status:null}` (no integration row yet — the
        // common case for a site that never finished connecting) is
        // NOT terminal here: we keep polling until the budget expires
        // and the consumer falls back to `'unconfirmed'`, which
        // re-enables the Connect button. Treating no-row as terminal
        // would gray the button for a fresh site.
      } catch {
        /* swallow — keep polling until budget exhausts */
      }
      if (Date.now() - started > MAX_MS) {
        // Budget exhausted without a confirmed terminal — surface an
        // actionable state. A frozen "Connecting…" with a stuck-
        // disabled button is the founder-flagged regression we are
        // explicitly defending against (ADR-0054 §0). `'unconfirmed'`
        // tells the consumer panel to fall back to the idle Connect
        // affordance with a subtle "Couldn't confirm" note.
        pollingRef.current = false;
        setPolling(false);
        setConnectStatus('unconfirmed');
        // Defect D: same one-shot flag cleanup. After 30s of no
        // confirmation the merchant should see a CLICKABLE Connect
        // button and a "Couldn't confirm" affordance; the URL flag
        // must not survive into the next attempt.
        clearOauthReturnFlag();
        return;
      }
      setTimeout(() => {
        void tick();
      }, STEP_MS);
    };
    void tick();
  }, [api, site_id]);

  useEffect(() => {
    if (returnedFromOauth && site_id !== null) {
      setConnectStatus('connecting');
      startPolling();
    }
    return (): void => {
      pollingRef.current = false;
    };
  }, [returnedFromOauth, site_id, startPolling]);

  return { returnedFromOauth, connectStatus, polling };
}
